import React, { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { 
  FiActivity, 
  FiMonitor,
  FiTarget,
  FiPackage,
  FiUsers,
  FiShield,
  FiHeart,
  FiTrendingUp,
  FiClock,
  FiGlobe,
  FiCpu,
  FiCheckCircle,
  FiDatabase,
  FiAlertTriangle,
  FiXCircle,
  FiCreditCard,
  FiMapPin,
  FiFileText,
  FiShare2,
  FiSearch,
  FiShoppingCart,
  FiLink,
  FiArrowRightCircle,
  FiStar,
  FiGrid,
  FiUser,
  FiTruck,
  FiPlusSquare,
  FiShoppingBag,
  FiLayers,
  FiRadio,
  FiDollarSign,
  FiFolder,
  FiRepeat,
  FiBriefcase,
  FiLink2
} from "react-icons/fi";
import TopBanner from "../../../components/topBanner/topBanner";
import bannerImg from "../../../images/banner/banner---gioi-thieu.png";
import esmImg from "../../../images/banner/esm.jpg";
import logoEsm from "../../../images/Logo/logoESM.png";
import "swiper/css/navigation";
import "swiper/css/pagination";
import EsmFlowchart from "./EsmFlowchart";
import EsmImplementation from "./EsmImplementation";
import EsmCorrelationDiagram from "../introduce/diagrams/EsmCorrelationDiagram";
import DataSystemModel from "../introduce/diagrams/DataSystemModel";
import BusinessModelTabs from "../introduce/diagrams/BusinessModelTabs";
import "./EsmPresentation.scss";

// Custom Node for React Flow
const PresentationNode = ({ data }) => {
  return (
    <div className={`pres-node ${data.variant}`}>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} style={{ opacity: 0 }} />
      
      <div className="pres-icon">{data.icon}</div>
      <div className="pres-label" dangerouslySetInnerHTML={{ __html: data.label }}></div>
    </div>
  );
};

const nodeTypes = {
  custom: PresentationNode,
};

const EsmPresentation = () => {
  const { t, i18n } = useTranslation();

  // Flow Nodes
  const initialNodes = useMemo(() => {
    return [
      {
        id: "center-esm",
        type: "custom",
        position: { x: 350, y: 250 },
        data: { variant: "center", icon: <FiCpu />, label: t("esm_pres.nodes.center") },
        draggable: false,
      },
      {
        id: "patient",
        type: "custom",
        position: { x: 50, y: 250 },
        data: { variant: "primary", icon: <FiUsers />, label: t("esm_pres.nodes.patient") },
        draggable: false,
      },
      {
        id: "hospital",
        type: "custom",
        position: { x: 350, y: 50 },
        data: { variant: "secondary", icon: <FiActivity />, label: t("esm_pres.nodes.hospital") },
        draggable: false,
      },
      {
        id: "clinic",
        type: "custom",
        position: { x: 650, y: 250 },
        data: { variant: "secondary", icon: <FiHeart />, label: t("esm_pres.nodes.clinic") },
        draggable: false,
      },
      {
        id: "pharmacy",
        type: "custom",
        position: { x: 350, y: 450 },
        data: { variant: "secondary", icon: <FiPackage />, label: t("esm_pres.nodes.pharmacy") },
        draggable: false,
      },
      {
        id: "doctor",
        type: "custom",
        position: { x: 650, y: 50 },
        data: { variant: "tertiary", icon: <FiTarget />, label: t("esm_pres.nodes.doctor") },
        draggable: false,
      }
    ];
  }, [t]);

  // Flow Edges
  const initialEdges = useMemo(() => {
    const edgeStyle = { stroke: "#23a84f", strokeWidth: 2, animation: "dash 1s linear infinite" };
    return [
      { id: "e1", source: "patient", target: "center-esm", type: "smoothstep", animated: true, style: edgeStyle },
      { id: "e2", source: "center-esm", target: "hospital", type: "smoothstep", animated: true, style: edgeStyle },
      { id: "e3", source: "center-esm", target: "clinic", type: "smoothstep", animated: true, style: edgeStyle },
      { id: "e4", source: "center-esm", target: "pharmacy", type: "smoothstep", animated: true, style: edgeStyle },
      { id: "e5", source: "hospital", target: "doctor", type: "smoothstep", animated: true, style: edgeStyle },
      { id: "e6", source: "clinic", target: "doctor", type: "smoothstep", animated: true, style: edgeStyle },
    ];
  }, []);

  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges]);

  // 10 Benefits Data
  const benefits = [
    { icon: <FiUsers />, key: "benefit1" },
    { icon: <FiTrendingUp />, key: "benefit2" },
    { icon: <FiShoppingBag />, key: "benefit3" },
    { icon: <FiLayers />, key: "benefit4" },
    { icon: <FiShare2 />, key: "benefit5" },
    { icon: <FiRadio />, key: "benefit6" },
    { icon: <FiGlobe />, key: "benefit7" },
    { icon: <FiDollarSign />, key: "benefit8" },
    { icon: <FiFolder />, key: "benefit9" },
    { icon: <FiCpu />, key: "benefit10" },
  ];

  return (
    <>
      <TopBanner
        titleCenter={t("esm_pres.page_title")}
        pageName={t("esm_pres.breadcrumb_parent")}
        text={t("esm_pres.breadcrumb_current")}
        imgBanner={bannerImg}
      />
      <div className="esm-pres-page">
        
        {/* HERO SECTION */}
        <section className="ep-hero">
          <div className="container">
            <div className="ep-hero-inner">
              <div className="ep-hero-text">
                <span className="ep-kicker">{t("esm_pres.hero_kicker")}</span>
                <h1 className="ep-title">{t("esm_pres.hero_title")}</h1>
                <p className="ep-desc">{t("esm_pres.hero_desc")}</p>
              </div>
              <div className="ep-hero-image">
                <img src={esmImg} alt="ESM Ecosystem" />
              </div>
            </div>
          </div>
        </section>

        {/* TRADITIONAL CONNECTIONS SECTION */}
        <section className="ep-traditional-section">
          <div className="container">
            <div className="ep-section-header">
              <h2>{t("esm_pres.traditional.title")}</h2>
              <p>{t("esm_pres.traditional.subtitle")}</p>
            </div>
            
            <div className="ep-trad-content">
              {/* Methods */}
              <div className="ep-trad-methods">
                <div className="tm-card">
                  <FiCreditCard className="tm-icon" />
                  <span>{t("esm_pres.traditional.methods.bhyt")}</span>
                </div>
                <div className="tm-card">
                  <FiShield className="tm-icon" />
                  <span>{t("esm_pres.traditional.methods.insurance")}</span>
                </div>
                <div className="tm-card">
                  <FiGlobe className="tm-icon" />
                  <span>{t("esm_pres.traditional.methods.communication")}</span>
                </div>
                <div className="tm-card">
                  <FiShare2 className="tm-icon" />
                  <span>{t("esm_pres.traditional.methods.social")}</span>
                </div>
                <div className="tm-card">
                  <FiUsers className="tm-icon" />
                  <span>{t("esm_pres.traditional.methods.referral")}</span>
                </div>
                <div className="tm-card">
                  <FiMapPin className="tm-icon" />
                  <span>{t("esm_pres.traditional.methods.radius")}</span>
                </div>
                <div className="tm-card">
                  <FiSearch className="tm-icon" />
                  <span>{t("esm_pres.traditional.methods.gprs")}</span>
                </div>
              </div>

              {/* Limitations */}
              <div className="ep-trad-limitations">
                <div className="ts-badge-trad">
                  {t("esm_pres.roles_and_features.badge_problem")}
                </div>
                <h3>
                  <FiAlertTriangle className="alert-icon" /> 
                  {t("esm_pres.traditional.limitations_title")}
                </h3>
                <ul className="limit-list">
                  <li>
                    <FiXCircle className="limit-icon" />
                    <span>{t("esm_pres.traditional.limitations.l1")}</span>
                  </li>
                  <li>
                    <FiXCircle className="limit-icon" />
                    <span>{t("esm_pres.traditional.limitations.l2")}</span>
                  </li>
                  <li>
                    <FiXCircle className="limit-icon" />
                    <span>{t("esm_pres.traditional.limitations.l3")}</span>
                  </li>
                  <li>
                    <FiXCircle className="limit-icon" />
                    <span>{t("esm_pres.traditional.limitations.l4")}</span>
                  </li>
                  <li>
                    <FiXCircle className="limit-icon" />
                    <span>{t("esm_pres.traditional.limitations.l5")}</span>
                  </li>
                  <li>
                    <FiXCircle className="limit-icon" />
                    <span>{t("esm_pres.traditional.limitations.l6")}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ROLES AND CORRELATIONS SECTION */}
        <section className="ep-roles-section">
          <div className="container">
            <div className="ep-section-header">
              <h2>{t("esm_pres.roles_and_features.role_title")}</h2>
            </div>
            
            {/* Unified Roles & Correlations Box */}
            <div className="ep-roles-combined-box">
              {/* Patient vs Customer Dual Role Branching */}
              <div className="ep-dual-roles-wrapper branching-layout">
                <div className="dr-source-node">
                  <div className="dr-source-icon"><FiUser /></div>
                  <h4>{t("esm_pres.roles_and_features.role_source")}</h4>
                </div>

                <div className="dr-branching-arrows">
                  {/* CSS will handle the lines using pseudo elements or SVG */}
                  <div className="branch-line top-line"></div>
                  <div className="branch-line bottom-line"></div>
                </div>

                <div className="dr-target-nodes">
                  <div className="dr-card role-patient">
                    <div className="dr-icon-wrap"><FiActivity /></div>
                    <p>{t("esm_pres.roles_and_features.role_patient")}</p>
                  </div>
                  <div className="dr-card role-customer">
                    <div className="dr-icon-wrap"><FiShoppingCart /></div>
                    <p>{t("esm_pres.roles_and_features.role_customer")}</p>
                  </div>
                </div>
              </div>

              {/* Connector line between sections */}
              <div className="ep-roles-connector"></div>

              {/* Ecosystem Correlations */}
              <div className="ep-ecosystem-corr">
                <h3>{t("esm_pres.roles_and_features.ecosystem_corr_title")}</h3>
                <div className="eco-list wow-grid">
                  <div className="eco-item">
                    <div className="eco-icon-wrap bg-blue"><FiActivity /></div>
                    <span>{t("esm_pres.roles_and_features.ecosystem_corr_1")}</span>
                  </div>
                  <div className="eco-item">
                    <div className="eco-icon-wrap bg-green"><FiShield /></div>
                    <span>{t("esm_pres.roles_and_features.ecosystem_corr_2")}</span>
                  </div>
                  <div className="eco-item">
                    <div className="eco-icon-wrap bg-purple"><FiHeart /></div>
                    <span>{t("esm_pres.roles_and_features.ecosystem_corr_3")}</span>
                  </div>
                  <div className="eco-item">
                    <div className="eco-icon-wrap bg-orange"><FiUsers /></div>
                    <span>{t("esm_pres.roles_and_features.ecosystem_corr_4")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4 QUADRANT FEATURES SECTION */}
        <section className="ep-quadrant-section">
          <div className="container">
            <div className="ep-section-header">
              <h2>{t("esm_pres.roles_and_features.feature_title")}</h2>
            </div>
            
            <div className="ep-4-grid">
              {/* Center Logo/Concept */}
              <div className="ep-grid-center">
                <div className="center-orb">
                  <img src={logoEsm} alt="ESM Logo" className="orb-logo" />
                </div>
              </div>
              
              {/* Q1: Hospital */}
              <div className="quad-card q-hospital">
                <div className="q-header">
                  <FiActivity className="q-icon" />
                  <h4>{t("esm_pres.roles_and_features.f_hospital.title")}</h4>
                </div>
                <ul>
                  {t("esm_pres.roles_and_features.f_hospital.items", { returnObjects: true }).map((item, idx) => (
                    <li key={idx}><FiArrowRightCircle className="li-icon"/> {item}</li>
                  ))}
                </ul>
              </div>

              {/* Q2: Clinic */}
              <div className="quad-card q-clinic">
                <div className="q-header">
                  <FiHeart className="q-icon" />
                  <h4>{t("esm_pres.roles_and_features.f_clinic.title")}</h4>
                </div>
                <ul>
                  {t("esm_pres.roles_and_features.f_clinic.items", { returnObjects: true }).map((item, idx) => (
                    <li key={idx}><FiArrowRightCircle className="li-icon"/> {item}</li>
                  ))}
                </ul>
              </div>

              {/* Q3: Doctor */}
              <div className="quad-card q-doctor">
                <div className="q-header">
                  <FiTarget className="q-icon" />
                  <h4>{t("esm_pres.roles_and_features.f_doctor.title")}</h4>
                </div>
                <ul>
                  {t("esm_pres.roles_and_features.f_doctor.items", { returnObjects: true }).map((item, idx) => (
                    <li key={idx}><FiArrowRightCircle className="li-icon"/> {item}</li>
                  ))}
                </ul>
              </div>

              {/* Q4: Patient */}
              <div className="quad-card q-patient">
                <div className="q-header">
                  <FiUsers className="q-icon" />
                  <h4>{t("esm_pres.roles_and_features.f_patient.title")}</h4>
                </div>
                <ul>
                  {t("esm_pres.roles_and_features.f_patient.items", { returnObjects: true }).map((item, idx) => (
                    <li key={idx}><FiArrowRightCircle className="li-icon"/> {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* TECHNOLOGY SHIFT SECTION */}
        <section className="ep-tech-shift-section">
          <div className="container">
            <div className="ep-section-header">
              <h2>{t("esm_pres.roles_and_features.tech_shift_title")}</h2>
            </div>
            
            <div className="ep-ts-container">
              {/* Problem / Cause */}
              <div className="ts-card problem-card">
                <div className="ts-badge">{t("esm_pres.roles_and_features.badge_problem")}</div>
                <div className="ts-icon-wrapper"><FiAlertTriangle /></div>
                <h4>{t("esm_pres.roles_and_features.tech_overload.title")}</h4>
                <p>{t("esm_pres.roles_and_features.tech_overload.desc")}</p>
              </div>
              
              {/* Transformation */}
              <div className="ts-arrow-container">
                <div className="ts-pulse-line"></div>
                <div className="ts-arrow">
                  <FiArrowRightCircle />
                </div>
                <div className="ts-pulse-line"></div>
              </div>
              
              {/* Solution */}
              <div className="ts-card solution-card">
                <div className="ts-badge">{t("esm_pres.roles_and_features.badge_solution")}</div>
                <div className="ts-icon-wrapper"><FiCheckCircle /></div>
                <h4>{t("esm_pres.roles_and_features.tech_solution.title")}</h4>
                <p>{t("esm_pres.roles_and_features.tech_solution.desc")}</p>
              </div>
            </div>
          </div>
        </section>

        {/* PLATFORM INTRO SECTION (SLIDE 1) */}
        <section className="ep-platform-intro-section">
          <div className="container">
            <div className="ep-pi-showcase-card">
              {/* Top Tech Badge */}
              <div className="ep-pi-badge">
                <FiCpu className="badge-icon" />
                <span>{t("esm_pres.platform_intro.title")}</span>
              </div>

              {/* Central Glowing Platform Logo Showcase */}
              <div className="ep-pi-hero-center">
                <div className="ep-pi-platform-ring"></div>
                <div className="ep-pi-logo-container">
                  <img src={logoEsm} alt="ESM Platform Logo" className="ep-pi-logo-img" />
                </div>
              </div>

              {/* Intro Narrative Description */}
              <div className="ep-pi-content">
                <p className="ep-pi-desc-text">
                  {t("esm_pres.platform_intro.desc")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 10 BENEFITS SECTION (SLIDE 3: RADIATING HEXAGON / DRIBBLE LAYOUT) */}
        <section className="ep-benefits-section">
          <div className="container">
            <div className="ep-section-header">
              <h2>{t("esm_pres.benefits_title")}</h2>
            </div>
            
            <div className="ep-orbit-layout">
              {/* Center Core */}
              <div className="orbit-center-core">
                <div className="core-orbit-ring ring-1"></div>
                <div className="core-orbit-ring ring-2"></div>
                <div className="core-orbit-ring ring-3"></div>
                <div className="core-orb">
                  <img src={logoEsm} alt="ESM Ecosystem" className="core-logo" />
                </div>
              </div>

              {/* Single-transform Ferris Wheel */}
              <div className="orbit-wheel">
                {benefits.map((b, idx) => {
                  const angle = (360 / 10) * idx;
                  return (
                    <div 
                      className="orbit-node" 
                      style={{ '--static-angle': `${angle}deg` }} 
                      key={idx}
                    >
                      <div className="orbit-card">
                        <div className={`oc-icon ${idx < 5 ? 'bg-blue' : 'bg-green'}`}>
                          {b.icon}
                        </div>
                        <div className="oc-text">
                          {t(`esm_pres.benefits.${b.key}`)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* REACT FLOW DIAGRAM SECTION (SLIDE 2) */}
        <section className="ep-flowchart-section">
          <div className="container" style={{ maxWidth: '1300px' }}>
            <EsmFlowchart />
          </div>
        </section>

        {/* CORRELATION DIAGRAM SECTION (NEW) */}
        <section className="ep-correlation-diagram-section" style={{ paddingBottom: '60px' }}>
          <div className="container" style={{ maxWidth: '1300px' }}>
            <EsmCorrelationDiagram />
          </div>
        </section>

        {/* ESM IMPLEMENTATION SECTION */}
        <section className="ep-esm-implementation-section">
          <div className="container" style={{ maxWidth: '1300px' }}>
            <EsmImplementation />
          </div>
        </section>

        {/* DATA SYSTEM MODEL SECTION */}
        <section className="ep-data-system-model-section">
          <div className="container" style={{ maxWidth: '1300px' }}>
            <DataSystemModel />
          </div>
        </section>

        {/* BUSINESS MODEL TABS SECTION */}
        <section className="ep-business-model-tabs-section" style={{ paddingBottom: '80px' }}>
          <div className="container" style={{ maxWidth: '1300px' }}>
            <BusinessModelTabs />
          </div>
        </section>

      </div>
    </>
  );
};

export default EsmPresentation;
